import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../api";
import { MIGRATION } from "../../../constants/testIds";
import { useAppData } from "../../../contexts/AppDataContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { SubmitButton } from "../../common/SubmitButton";

const DEVICE_TYPES = [
  { key: "qr", label: "QR code scanner" }, { key: "face", label: "Face recognition" },
  { key: "biometric", label: "Biometric fingerprint" }, { key: "turnstile", label: "Turnstile gate" },
];

function StatusChip({ status, reason }) {
  const ok = status === "active";
  return (
    <div className="flex items-center gap-1.5 text-xs" title={reason || ""}>
      {ok ? <CheckCircle2 size={13} style={{ color: "#3FA65B" }} /> : <XCircle size={13} style={{ color: "#D64545" }} />}
      <span style={{ color: ok ? "#3FA65B" : "#D64545" }}>{status}</span>
    </div>
  );
}

// SCR-P04-30 - one screen, three connectors: WhatsApp, payment gateway, gate devices. Every
// "Test" is a real (or honestly-not-live) check - never a faked success.
export function IntegrationConnectTab() {
  const queryClient = useQueryClient();
  const { branches } = useAppData();

  const { data: configs } = useQuery({ queryKey: ["integration-configs"], queryFn: () => api.get("/integration-configs").then((r) => r.data.configs) });
  const { data: devices } = useQuery({ queryKey: ["devices"], queryFn: () => api.get("/devices").then((r) => r.data.devices) });

  const whatsapp = configs?.find((c) => c.channel === "whatsapp");
  const payment = configs?.find((c) => c.channel === "payment_gateway");

  const connectMutation = useMutation({
    mutationFn: async ({ channel, provider, display_name, from_address, public_config, secret }) => {
      const created = await api.post("/integration-configs", { channel, provider, display_name, from_address, public_config });
      await api.post(`/integration-configs/${created.data.id}/secret`, { secret });
      return created.data;
    },
    onSuccess: () => { toast.success("Connected - run Test to verify"); queryClient.invalidateQueries({ queryKey: ["integration-configs"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Connect failed"),
  });

  const testMutation = useMutation({
    mutationFn: (configId) => api.post(`/integration-configs/${configId}/test`),
    onSuccess: (r) => { toast[r.data.status === "active" ? "success" : "error"](r.data.status_reason || `Test result: ${r.data.status}`); queryClient.invalidateQueries({ queryKey: ["integration-configs"] }); },
  });

  const registerDeviceMutation = useMutation({
    mutationFn: (body) => api.post("/devices", body),
    onSuccess: () => { toast.success("Device registered - run Test to check connectivity"); queryClient.invalidateQueries({ queryKey: ["devices"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Could not register device"),
  });

  const testDeviceMutation = useMutation({
    mutationFn: (id) => api.post(`/devices/${id}/test`),
    onSuccess: (r) => { toast.error(r.data.status_reason); queryClient.invalidateQueries({ queryKey: ["devices"] }); },
  });

  return (
    <div className="space-y-4">
      <WhatsappSection existing={whatsapp} onConnect={connectMutation.mutate} pending={connectMutation.isPending} onTest={() => testMutation.mutate(whatsapp.id)} testPending={testMutation.isPending} />
      <PaymentSection existing={payment} onConnect={connectMutation.mutate} pending={connectMutation.isPending} onTest={() => testMutation.mutate(payment.id)} testPending={testMutation.isPending} />
      <DeviceSection branches={branches} devices={devices} onRegister={registerDeviceMutation.mutate} pending={registerDeviceMutation.isPending} onTest={(id) => testDeviceMutation.mutate(id)} />
    </div>
  );
}

function WhatsappSection({ existing, onConnect, pending, onTest, testPending }) {
  const [displayName, setDisplayName] = useState("");
  const [number, setNumber] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [token, setToken] = useState("");

  return (
    <div className="solid-surface rounded-[14px] p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>WhatsApp (Meta Cloud API)</p>
        {existing && <StatusChip status={existing.status} reason={existing.status_reason} />}
      </div>
      {existing ? (
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: "var(--scarlet-text-secondary)" }}>{existing.display_name} &middot; token ending {existing.secret?.secret_preview}</span>
          <Button data-testid={MIGRATION.integrationWhatsappTest} size="sm" variant="outline" disabled={testPending} onClick={onTest}>Test connection</Button>
        </div>
      ) : (
        <div data-testid={MIGRATION.integrationWhatsappForm} className="grid grid-cols-2 gap-2.5">
          <Input data-testid={MIGRATION.integrationWhatsappDisplayName} placeholder="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <Input data-testid={MIGRATION.integrationWhatsappNumber} placeholder="WhatsApp number" value={number} onChange={(e) => setNumber(e.target.value)} />
          <Input data-testid={MIGRATION.integrationWhatsappWabaId} placeholder="WABA ID" value={wabaId} onChange={(e) => setWabaId(e.target.value)} />
          <Input data-testid={MIGRATION.integrationWhatsappPhoneId} placeholder="Phone number ID" value={phoneId} onChange={(e) => setPhoneId(e.target.value)} />
          <Input data-testid={MIGRATION.integrationWhatsappToken} placeholder="Access token" value={token} onChange={(e) => setToken(e.target.value)} className="col-span-2" />
          <div className="col-span-2">
            <SubmitButton
              testId={MIGRATION.integrationWhatsappSubmit} pending={pending} label="Connect WhatsApp"
              disabled={!displayName || !number || !wabaId || !phoneId || !token}
              onClick={() => onConnect({
                channel: "whatsapp", provider: "meta_cloud_api", display_name: displayName, from_address: number,
                public_config: { waba_id: wabaId, phone_number_id: phoneId }, secret: { access_token: token },
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentSection({ existing, onConnect, pending, onTest, testPending }) {
  const [provider, setProvider] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [key, setKey] = useState("");

  return (
    <div className="solid-surface rounded-[14px] p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Payment gateway</p>
        {existing && <StatusChip status={existing.status} reason={existing.status_reason} />}
      </div>
      {existing ? (
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: "var(--scarlet-text-secondary)" }}>{existing.display_name} ({existing.provider})</span>
          <Button data-testid={MIGRATION.integrationPaymentTest} size="sm" variant="outline" disabled={testPending} onClick={onTest}>Test connection</Button>
        </div>
      ) : (
        <div data-testid={MIGRATION.integrationPaymentForm} className="grid grid-cols-2 gap-2.5">
          <Input data-testid={MIGRATION.integrationPaymentProvider} placeholder="Provider (e.g. razorpay, stripe)" value={provider} onChange={(e) => setProvider(e.target.value)} />
          <Input data-testid={MIGRATION.integrationPaymentDisplayName} placeholder="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <Input data-testid={MIGRATION.integrationPaymentKey} placeholder="API key" value={key} onChange={(e) => setKey(e.target.value)} className="col-span-2" />
          <div className="col-span-2">
            <SubmitButton
              testId={MIGRATION.integrationPaymentSubmit} pending={pending} label="Connect payment gateway"
              disabled={!provider || !displayName || !key}
              onClick={() => onConnect({ channel: "payment_gateway", provider, display_name: displayName, from_address: "n/a", public_config: {}, secret: { api_key: key } })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DeviceSection({ branches, devices, onRegister, pending, onTest }) {
  const [type, setType] = useState("qr");
  const [label, setLabel] = useState("");
  const [branchId, setBranchId] = useState("");
  const [tokenVal, setTokenVal] = useState("");

  return (
    <div className="solid-surface rounded-[14px] p-5 space-y-3">
      <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Gate devices</p>
      <div className="grid grid-cols-2 gap-2.5">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid={MIGRATION.deviceTypeSelect}><SelectValue /></SelectTrigger>
          <SelectContent>{DEVICE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger data-testid={MIGRATION.deviceBranchSelect}><SelectValue placeholder="Branch" /></SelectTrigger>
          <SelectContent>{branches?.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid={MIGRATION.deviceLabelInput} placeholder="Label (e.g. Front gate)" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Input data-testid={MIGRATION.deviceTokenInput} placeholder="Device token" value={tokenVal} onChange={(e) => setTokenVal(e.target.value)} />
      </div>
      <SubmitButton
        testId={MIGRATION.deviceRegisterButton} pending={pending} label="Register device"
        disabled={!label || !branchId || !tokenVal}
        onClick={() => onRegister({ type, label, branch_id: branchId, device_token: tokenVal })}
      />
      <div className="space-y-1.5 pt-2">
        {devices?.map((d) => (
          <div key={d.id} data-testid={MIGRATION.deviceRow(d.id)} className="flex items-center justify-between text-sm border-t pt-1.5" style={{ borderColor: "var(--scarlet-hairline)" }}>
            <span style={{ color: "var(--scarlet-text)" }}>{d.label} ({d.type})</span>
            <div className="flex items-center gap-2">
              <StatusChip status={d.status} reason={d.status_reason} />
              <button data-testid={MIGRATION.deviceTestButton(d.id)} onClick={() => onTest(d.id)} className="text-xs underline" style={{ color: "var(--scarlet-accent)" }}>Test</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
